import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Global page-transition overlay.
 * ─────────────────────────────
 * Renders a single full-viewport black overlay that fades in → navigates →
 * fades out around any route change requested via `transitionTo(path)`.
 *
 * Communicates via `window.__lfTransitionTo(path)` (assigned by this component
 * on mount) so any caller — regardless of how deep or lazily-loaded — can fire
 * it without needing a React context that survives suspense / bundle splits.
 *
 * Also handles the initial page-load fade: overlay starts opaque, fades out
 * after mount. Replaces the per-page `<PageBlackOverlay />` splash.
 *
 * Timing (default routes):
 *   • Initial-load fade-out : 800ms
 *   • Nav fade-in           : 700ms
 *   • Route swap hold       : 60ms
 *   • Nav fade-out          : 720ms
 *
 * Studio routes (path starts with `/studio`) use a slower cadence that gives
 * the studio splash time to settle:
 *   • Nav fade-in           : 1000ms
 *   • Route swap hold       : 500ms
 *   • Nav fade-out          : 1000ms
 */
export default function PageTransition({ children }) {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("initial-in");
  const [dur, setDur] = useState({ in: 700, out: 720 });
  const timers = useRef([]);

  const clearAll = () => { timers.current.forEach(clearTimeout); timers.current = []; };

  useEffect(() => {
    // Initial-load: hold opaque one frame, then fade to transparent.
    const t = setTimeout(() => setPhase("idle"), 60);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const transitionTo = (path) => {
      if (!path) return;
      clearAll();
      const slow = typeof path === "string" && path.toLowerCase().startsWith("/studio");
      const IN = slow ? 1000 : 700;
      const HOLD = slow ? 200 : 60;
      const OUT = slow ? 1000 : 720;
      setDur({ in: IN, out: OUT });
      setPhase("fading-in");
      timers.current.push(setTimeout(() => {
        setPhase("hold");
        navigate(path);
        timers.current.push(setTimeout(() => {
          setPhase("fading-out");
          timers.current.push(setTimeout(() => setPhase("idle"), OUT));
        }, HOLD));
      }, IN));
    };
    // eslint-disable-next-line no-underscore-dangle
    window.__lfTransitionTo = transitionTo;
    return () => {
      // eslint-disable-next-line no-underscore-dangle
      if (window.__lfTransitionTo === transitionTo) delete window.__lfTransitionTo;
      clearAll();
    };
  }, [navigate]);

  const opaque = phase === "initial-in" || phase === "fading-in" || phase === "hold";

  return (
    <>
      {children}
      {phase !== "idle" && (
        <div
          data-testid="page-transition-overlay"
          aria-hidden="true"
          style={{
            position: "fixed", inset: 0,
            background: "#000",
            zIndex: 99999,
            opacity: opaque ? 1 : 0,
            transition: `opacity ${phase === "initial-in" ? 800 : (phase === "fading-in" ? dur.in : dur.out)}ms cubic-bezier(0.4, 0, 0.2, 1)`,
            pointerEvents: opaque && phase !== "initial-in" ? "auto" : "none",
          }}
        />
      )}
    </>
  );
}

/**
 * Small helper for consumers: fires the transition if available, otherwise
 * falls back to a straight navigate (via the passed `fallback` fn) so nothing
 * ever gets stuck if the overlay hasn't mounted yet.
 */
export function pageTransitionTo(path, fallback) {
  // eslint-disable-next-line no-underscore-dangle
  const t = typeof window !== "undefined" && window.__lfTransitionTo;
  if (t) t(path);
  else if (fallback) fallback(path);
}
